"use client";

import { AdyenCheckout, Dropin, Card } from "@adyen/adyen-web";
import type { CoreConfiguration } from "@adyen/adyen-web";
import { useEffect, useState } from "react";
import { useValpayConfigQuery } from "../../graphql/types";
import ConcentricLoader from "../Loader/ConcentricLoader";
import PaymentMethods from "./PaymentMethods";

interface ValpaySubmitData {
  name?: string;
  token?: string;
  numberLast4?: string;
  expiration?: string;
  cardBrand?: string;
  encryptedCardNumber: string;
  encryptedExpiryMonth: string;
  encryptedExpiryYear: string;
  encryptedSecurityCode: string;
  postalCode?: string;
}

interface ValpayProps {
  buttonLabel?: string;
  buttonSubmitting?: boolean;
  className?: string;
  hideCvv?: boolean;
  hideNewPaymentMethod?: boolean;
  onDelete?: (id: string) => Promise<boolean>;
  onSubmit: (data: ValpaySubmitData, onResult: (success: boolean) => void) => void;
  onPaymentFailed?: (error: { resultCode: string; message?: string }) => void;
  onError?: (error: { name: string; message: string }) => void;
  processorId?: number;
  profileMode?: boolean;
  shopperReference?: string;
  showZip?: boolean;
}

function Valpay({
  buttonLabel,
  buttonSubmitting = false,
  className,
  hideCvv,
  hideNewPaymentMethod,
  onDelete,
  onSubmit,
  onPaymentFailed,
  onError: onErrorProp,
  processorId,
  shopperReference,
  showZip,
  profileMode = false,
}: ValpayProps) {
  const [valpayObj, setValpayObj] = useState<InstanceType<typeof Dropin> | null>(null);
  const [valpayError, setError] = useState<string>();
  const { data, loading, error } = useValpayConfigQuery({
    variables: { processorId, shopperReference },
  });

  useEffect(() => {
    if (!buttonSubmitting && valpayObj) {
      valpayObj.setStatus("ready");
    }
  }, [buttonSubmitting, valpayObj]);

  const valpaySession = data ? data.valpayConfig : undefined;
  useEffect(() => {
    if (valpaySession && !valpaySession.enabled) {
      // Use the backend error message if available, otherwise show generic message
      setError(valpaySession.error || "Valpay is not enabled for this site.");
      return;
    }

    // Only initiate ValPay if we are ready to do so.
    if (!valpaySession) return;

    let dropin: InstanceType<typeof Dropin> | null = null;

    async function initializeValpay() {
      // Parse payment methods with error handling
      let paymentMethodsResponse;
      try {
        paymentMethodsResponse = JSON.parse(valpaySession!.paymentMethods!);
      } catch (e) {
        console.error("Failed to parse paymentMethods:", e);
        setError("Payment configuration error. Please try again later.");
        return;
      }

      // Validate the parsed response has expected structure
      if (!paymentMethodsResponse || typeof paymentMethodsResponse !== "object") {
        console.error("Invalid paymentMethods structure:", paymentMethodsResponse);
        setError("Payment configuration error. Please try again later.");
        return;
      }

      let translations = {};
      if (buttonLabel) {
        translations = {
          en_US: {
            payButton: buttonLabel,
          },
        };
      }

      // Core checkout configuration (v6)
      const configuration: CoreConfiguration = {
        environment: valpaySession!.environment as CoreConfiguration["environment"],
        clientKey: valpaySession!.clientKey!,
        countryCode: "US", // Required in v6
        paymentMethodsResponse,
        translations,
        onError: (error, component) => {
          console.error(
            "PAYMENT ERROR",
            error.name,
            error.message,
            error.stack,
            component,
          );

          // Notify consumer so they can display an error message
          onErrorProp?.({ name: error.name, message: error.message });

          // Also call onPaymentFailed if provided (unified error handling)
          onPaymentFailed?.({
            resultCode: "Error",
            message: error.message || "A payment error occurred. Please try again.",
          });

          component?.setStatus("ready");
        },
        onPaymentFailed: (result, component) => {
          if (result) {
            onPaymentFailed?.({
              resultCode: result.resultCode,
              message: "message" in result ? (result as { message?: string }).message : undefined,
            });
          }
          component?.setStatus("ready");
        },
        onSubmit: (state, component) => {
          // Check state.isValid before processing
          if (!state.isValid) {
            console.warn("Adyen onSubmit called with invalid state:", state);
            component.setStatus("ready");
            return;
          }

          const onResult = (success: boolean) => {
            if (!success) {
              // On failure: reset to ready so the user can retry
              component.setStatus("ready");
            }
            // On success: leave the Drop-in in its loading state to prevent
            // double-submits. The buttonSubmitting useEffect will reset it
            // to "ready" when the parent is done. This also avoids showing
            // Adyen's built-in "Payment Successful" screen (which was
            // triggered by actions.resolve) since we're only collecting
            // card details, not charging.
          };

          // Stored cards expose their last 4, brand, and expiry in the
          // /paymentMethods response, keyed by the same id Adyen hands back as
          // storedPaymentMethodId on submit; the submit payload itself omits
          // them. Look them up so the back-end can record the card (newly typed
          // cards have no stored id and never expose these to the browser).
          // We deliberately drop the holder name for stored cards: Adyen sends
          // "Checkout Shopper PlaceHolder" as the holderName on the submit
          // itself (not the real name), so `name` is kept only for a newly
          // typed card (no stored id). Brand is the raw Adyen value (e.g.
          // "mc"); consumers map it to their own card-type codes.
          const storedPaymentMethodId = state.data.paymentMethod.storedPaymentMethodId;
          const storedMethods: Array<{
            id?: string;
            lastFour?: string;
            brand?: string;
            expiryMonth?: string;
            expiryYear?: string;
          }> = paymentMethodsResponse?.storedPaymentMethods ?? [];
          const storedCard = storedPaymentMethodId
            ? storedMethods.find((m) => m.id === storedPaymentMethodId)
            : undefined;
          // Adyen gives month/year separately; the back-end wants MMYYYY.
          const expiration =
            storedCard?.expiryMonth && storedCard?.expiryYear
              ? `${storedCard.expiryMonth.padStart(2, "0")}${storedCard.expiryYear}`
              : undefined;

          onSubmit(
            {
              name: storedPaymentMethodId ? undefined : state.data.paymentMethod.holderName,
              token: storedPaymentMethodId,
              numberLast4: storedCard?.lastFour,
              expiration,
              cardBrand: storedCard?.brand,
              encryptedCardNumber: state.data.paymentMethod.encryptedCardNumber,
              encryptedExpiryMonth: state.data.paymentMethod.encryptedExpiryMonth,
              encryptedExpiryYear: state.data.paymentMethod.encryptedExpiryYear,
              encryptedSecurityCode:
                state.data.paymentMethod.encryptedSecurityCode,
              postalCode: state.data.billingAddress?.postalCode,
            },
            onResult,
          );
        },
      };

      const checkout = await AdyenCheckout(configuration); // v6 uses factory function, not constructor

      // Drop-in configuration (v6 - paymentMethodsConfiguration moved here)
      const dropinConfiguration = {
        // v6 requires explicit component registration
        paymentMethodComponents: [Card],
        paymentMethodsConfiguration: {
          card: {
            styles: {
              base: {
                fontFamily:
                  "Nunito Sans, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue",
                fontSize: "14px",
                lineHeight: "21px",
                outline: "2px solid transparent !important",
                outlineOffset: "2px !important",
              },
              error: {
                outline: "2px solid transparent !important",
                outlineOffset: "2px !important",
              },
              placeholder: {
                color: "rgb(107 114 128)",
                opacity: "0.9",
              },
            },
            hasHolderName: true, // Show name field
            holderNameRequired: true, // Required name field
            billingAddressMode: "partial" as const,
            billingAddressRequired: showZip,
          },
          storedCard: {
            showPayButton: !profileMode,
            hideCVC: profileMode || hideCvv,
          },
        },
        onDisableStoredPaymentMethod: async (
          storedPaymentMethodId: string,
          resolve: () => void,
          reject: () => void,
        ) => {
          // If no delete handler provided, reject to unblock the UI
          if (!onDelete) {
            reject();
            return;
          }

          try {
            const result = await onDelete(storedPaymentMethodId);
            result ? resolve() : reject();
          } catch (e) {
            console.error("Error deleting stored payment method:", e);
            reject(); // CRITICAL: Must call reject on exception, or Drop-in hangs
          }
        },
        showRemovePaymentMethodButton: profileMode,
        showStoredPaymentMethods: true,
        showPaymentMethods: !hideNewPaymentMethod,
      };

      dropin = new Dropin(checkout, dropinConfiguration);
      setValpayObj(dropin);
    }
    initializeValpay();

    return () => {
      // Clean up on unmount or when valpaySession changes
      if (dropin) {
        try {
          dropin.unmount();
        } catch (e) {
          // Ignore cleanup errors
        }
      }
      setValpayObj(null);
    };
  }, [buttonLabel, hideCvv, hideNewPaymentMethod, profileMode, showZip, valpaySession]);

  if (error) {
    console.error(error);
    return null;
  }

  if (valpayError) {
    return (
      <div className="max-w-2xl px-4 py-10 mx-auto text-xl sm:py-10 sm:px-6 lg:px-8">
        <div className="text-red-500">{valpayError}</div>
      </div>
    );
  }

  return loading ? (
    <ConcentricLoader className="max-w-2xl px-4 py-10 mx-auto opacity-60 sm:py-10 sm:px-6 lg:px-8" />
  ) : (
    <PaymentMethods className={className} valpayObj={valpayObj} />
  );
}

export default Valpay;
export type { ValpaySubmitData };
